import { z } from "zod";
import {
  StorefrontOrderAddressSchema,
  type StorefrontOrderAddress,
} from "@bytebite/schemas/delivery";

export const ORDER_STATUS_VALUES = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "completed",
  "cancelled",
] as const;

export const CANCELLATION_REASON_VALUES = [
  "restaurant_rejected",
  "acceptance_timeout",
  "customer_cancelled",
  "system",
] as const;

export const ACCEPTANCE_ETA_MINUTE_VALUES = [15, 30, 45, 60] as const;

export const StorefrontOrderItemSchema = z.object({
  id: z.string().min(1),
  menuItemId: z.string().min(1),
  name: z.string().min(1),
  variantName: z.string().min(1).nullable(),
  quantity: z.number().int().min(1).max(99),
  unitPriceCents: z.number().int().min(0),
  lineTotalCents: z.number().int().min(0),
});

export const StorefrontOrderSchema = z.object({
  id: z.string().min(1),
  restaurantId: z.string().min(1),
  fulfillmentType: z.enum(["pickup", "delivery"]),
  orderStatus: z.enum(ORDER_STATUS_VALUES),
  paymentStatus: z.literal("unpaid"),
  paymentMethod: z.literal("cash"),
  customerName: z.string().min(1),
  customerPhone: z.string().min(1),
  note: z.string().nullable(),
  items: z.array(StorefrontOrderItemSchema),
  itemCount: z.number().int().min(0),
  subtotalCents: z.number().int().min(0),
  deliveryFeeCents: z.number().int().min(0),
  totalCents: z.number().int().min(0),
  address: StorefrontOrderAddressSchema.nullable(),
  createdAt: z.string().min(1),
  acceptanceDeadlineAt: z.string().min(1),
  etaMinutes: z.number().int().nullable(),
  cancellationReason: z.enum(CANCELLATION_REASON_VALUES).nullable(),
});

export type StorefrontOrderItem = z.infer<typeof StorefrontOrderItemSchema>;
export type StorefrontOrder = z.infer<typeof StorefrontOrderSchema>;
export type { StorefrontOrderAddress };

export const DashboardOrderListSchema = z.object({
  restaurantId: z.string().min(1),
  orders: z.array(StorefrontOrderSchema),
});

export type DashboardOrderList = z.infer<typeof DashboardOrderListSchema>;

const optionalNote = z
  .union([z.string(), z.null()])
  .optional()
  .transform((value) => {
    if (value === undefined || value === null) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  })
  .refine((value) => value === null || value.length <= 500, { message: "Note is too long" });

export const CheckoutBodySchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    phone: z.string().trim().min(1).max(32),
    note: optionalNote,
    restaurantId: z.string().optional(),
    fulfillmentType: z.enum(["pickup", "delivery"]).default("pickup"),
    providerId: z.string().trim().min(1).max(512).optional(),
    sessionToken: z.string().uuid().optional(),
  })
  .superRefine((value, ctx) => {
    if (value.fulfillmentType !== "delivery") return;
    if (!value.providerId || !value.sessionToken) {
      ctx.addIssue({ code: "custom", message: "Delivery address is required" });
    }
  });

export type CheckoutBody = z.infer<typeof CheckoutBodySchema>;

export const AcceptOrderBodySchema = z.object({
  etaMinutes: z.union([z.literal(15), z.literal(30), z.literal(45), z.literal(60)]),
  restaurantId: z.string().optional(),
});

export type AcceptOrderBody = z.infer<typeof AcceptOrderBodySchema>;

export const RejectOrderBodySchema = z.object({
  restaurantId: z.string().optional(),
});

export type RejectOrderBody = z.infer<typeof RejectOrderBodySchema>;

/** Kitchen writes: empty body, or ignored optional restaurantId (path wins). */
export const KitchenOrderBodySchema = z.object({
  restaurantId: z.string().optional(),
});

export type KitchenOrderBody = z.infer<typeof KitchenOrderBodySchema>;
